import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { Icon, useLayout, type IconName } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { useProject, useProjectRealtime } from "../data/queries";
import { ShellSlot } from "../layout/shell";
import { ApprovalBell } from "../approvals/Approvals";
import { QuestionBell } from "../questions/Questions";
import { useMockScenario } from "../dev/useMockScenario";
import { MobileFrame, type MobileTab } from "../mobile/MobileProject";

/**
 * 项目级外壳（所有 /p/:pid/** 页面共用）：
 * - 顶栏：项目面包屑 + 待处理角标（审批 / 提问）
 * - 订阅 project:<pid> 通道（agent、审批、任务、PR、邮件、提问的实时更新）
 * - mock 模式下 ?mock=collab 触发演示场景
 */
export function ProjectChrome({ pid, bells }: { pid: string; bells?: ReactNode }) {
  const { t } = useT();
  const project = useProject(pid);
  useProjectRealtime(pid);
  useMockScenario(pid);
  return (
    <>
      <ShellSlot name="actions">
        {bells}
        <QuestionBell pid={pid} />
        <ApprovalBell pid={pid} />
      </ShellSlot>
      <ShellSlot name="title">
        {project.data ? (
          <>
            <Link to={`/t/${project.data.tenantId}`} className="muted ellipsis">
              {t("project.title")}
            </Link>
            <span className="muted" aria-hidden="true">
              /
            </span>
            <Link to={`/p/${pid}`} className="ellipsis pcrumb">
              <strong>{project.data.name}</strong>
            </Link>
          </>
        ) : null}
      </ShellSlot>
    </>
  );
}

export type ProjectSection = "team" | "rooms" | "meetings" | "tasks" | "pulls" | "mail" | "assets";

const SECTIONS: { id: ProjectSection; icon: IconName; path: string }[] = [
  { id: "team", icon: "users", path: "" },
  { id: "rooms", icon: "chat", path: "/rooms" },
  { id: "meetings", icon: "mic", path: "/meetings" },
  { id: "tasks", icon: "board", path: "/tasks" },
  { id: "pulls", icon: "pr", path: "/pulls" },
  { id: "mail", icon: "mail", path: "/mail" },
  { id: "assets", icon: "image", path: "/assets" },
];

/** 桌面 / 平板：项目内导航条（团队 / 聊天室 / 任务 / PR / 邮件 / 资产）；手机上由底部 tab 代替 */
export function ProjectNav({ pid, active }: { pid: string; active: ProjectSection }) {
  const { t } = useT();
  return (
    <nav className="pnav" aria-label={t("pnav.label")}>
      {SECTIONS.map((s) => (
        <NavLink
          key={s.id}
          to={`/p/${pid}${s.path}`}
          end={s.id !== "rooms" && s.id !== "assets" && s.id !== "meetings"}
          className={`pnav__link${active === s.id ? " is-active" : ""}`}
          aria-current={active === s.id ? "page" : undefined}
          data-testid={`pnav-${s.id}`}
        >
          <Icon name={s.icon} size={16} />
          {t(`pnav.${s.id}`)}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * 协作页面（任务 / PR / 邮件）的框架：桌面、平板 = 项目导航条 + 内容；手机 = 内容 + 底部 tab（高亮 mobileTab）。
 */
export function CollabFrame({ pid, section, mobileTab = "work", children }: { pid: string; section: ProjectSection; mobileTab?: MobileTab; children: ReactNode }) {
  const mobile = useLayout() === "mobile";
  return (
    <>
      <ProjectChrome pid={pid} />
      {mobile ? (
        <MobileFrame pid={pid} active={mobileTab}>
          {children}
        </MobileFrame>
      ) : (
        <div className="pframe">
          <ProjectNav pid={pid} active={section} />
          <div className="pframe__body pframe__body--scroll">{children}</div>
        </div>
      )}
    </>
  );
}
